import { Table } from '@tanstack/react-table';
import React, { ReactNode } from 'react';

import { ResourceType } from '@/cip/_openapi/portfolio-optimizer';
import { DebouncedSearchInput } from '@/components/DebouncedSearchInput';
import { ExportResults } from '@/components/table/ExportResults';
import { ResetFilters } from '@/components/table/ResetFilters';
import { TableSettings } from '@/components/table/TableSettings';

import { AccountTableLayout } from './accountTableDefaults';

interface AccountTableToolbarProps<T> {
    table: Table<T>;
    resourceType: ResourceType;
    metaData?: Record<string, any>;
    additionalFilters: Array<{ id: string; value: any }>;
    excludeColumns: string[];
    onReset?: () => void;
    leftContent?: ReactNode;
    layout?: AccountTableLayout;
    extraControls?: ReactNode;
}

export function AccountTableToolbar<T>({
    table,
    resourceType,
    metaData,
    additionalFilters,
    excludeColumns,
    onReset,
    leftContent,
    layout = 'legacy',
    extraControls,
}: AccountTableToolbarProps<T>) {
    if (layout === 'new') {
        const columns = table
            .getAllLeafColumns()
            .filter((column) => !excludeColumns.includes(column.id) && !column.columnDef.meta?.isDisplayCol);
        return (
            <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4">
                    <DebouncedSearchInput
                        aria-label="Search by symbol"
                        placeholder="Search symbol…"
                        containerClassName="w-64"
                        className="h-10 rounded-lg border-stone-300"
                        value={(table.getColumn('symbol')?.getFilterValue() as string[] | undefined)?.[0] ?? ''}
                        onValueChange={(value) => {
                            table.getColumn('symbol')?.setFilterValue(value ? [value] : undefined);
                            table.setPageIndex(0);
                        }}
                    />
                    {extraControls}
                </div>
                <div className="flex items-center gap-2 [&_button]:h-10 [&_button]:rounded-lg [&_button]:px-4">
                    {table.getState().columnFilters.length > 0 && <ResetFilters table={table} />}
                    <TableSettings
                        table={table}
                        excludeColumns={excludeColumns}
                        onReset={onReset}
                        label={`Columns ${columns.filter((column) => column.getIsVisible()).length}/${columns.length}`}
                    />
                    <ExportResults
                        table={table}
                        resourceType={resourceType}
                        metaData={metaData}
                        additionalFilters={additionalFilters}
                        label="Export"
                        variant="default"
                    />
                </div>
            </div>
        );
    }
    return (
        <div className="mb-3 flex items-center justify-between gap-2 border-b pb-3">
            <div className="flex-1">{leftContent}</div>
            <div className="flex flex-wrap gap-2">
                <ExportResults
                    table={table}
                    resourceType={resourceType}
                    metaData={metaData}
                    additionalFilters={additionalFilters}
                />
                <ResetFilters table={table} />
                <TableSettings table={table} excludeColumns={excludeColumns} onReset={onReset} />
            </div>
        </div>
    );
}
